import { ChangeEvent, useEffect, useState } from "react";
import CreateOrEditProjectsModal from "./CreateOrEditProjectsModal";
import ShowProjectsModal from "./ShowProjectModal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { createProjectValidationSchema } from "../../../helpers/validation-schema";
import { santizeUrl } from "../../../helpers/sanitize-urls";
import { useSelector, useDispatch } from "react-redux";
import {
  UpdateProject,
  createProject,
  deleteProject,
  getAllProjectsForUser,
} from "../services/projects.service";
import { handleGetAllProjects } from "../../../reducers/project.reducer";
import { AppDispatch, RootState } from "../../../store";
import { setProject } from "../../../reducers/project.reducer";
import { useNavigate } from "react-router-dom";
import { handleGetPages, setURI } from "../../../reducers/uris.reducer";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal";

import { showPages } from "../services/pages.services";
import { PopUpinterface } from "../types/websiteInsitghts.types";
import { ProjectType } from "../types/project.types";
import { Project } from "../types/showProjectModal.types";
import { showToast } from "../../../helpers/notification";

const ProjectModal = () => {
  const INITIAL_PROJECT = {
    current_id: 0,
    is_active: 1,
    protocol: "http",
    host: "",
    reporting_frequency: 0,
  };

  const [projectData, setProjectData] = useState<ProjectType>(INITIAL_PROJECT);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalToShow, setModalToShow] = useState("");
  const [projects, setProjects] = useState({ data: [], cloneData: [] });
  const [editMode, setEditMode] = useState(0);
  const [dataFound, setDataFound] = useState("");
  const [currentProject, setCurrentProject] =
    useState<ProjectType>(INITIAL_PROJECT);
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [buttonLoader, setButtonLoader] = useState(false);
  const { projectInfo, account } = useSelector((state: RootState) => ({
    projectInfo: state.project.projectId,
    account: state.account,
  }));
  const values: ProjectType = projectData;
  const { accountPlanId } = useSelector((state: RootState) => ({
    accountPlanId: state.account.plan_id,
  }));
  const loadProjects = async () => {
    try {
      const response = await getAllProjectsForUser();
      setProjects({ data: response.data, cloneData: response.data });
    } catch (err) {}
  };
  useEffect(() => {
    if (modalToShow === "show") {
      loadProjects();
    }
  }, [modalToShow]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ProjectType>({
    values,
    resolver: yupResolver(createProjectValidationSchema),
  });

  useEffect(() => {
    if (showModal === false) {
      const rootElement = document.getElementById("root") as HTMLElement;
      rootElement.style.overflow = "visible";
    }
  }, [showModal]);

  const onSubmit = async (createProjectdata: PopUpinterface) => {
    const { is_active, protocol, host, reporting_frequency }: ProjectType =
      createProjectdata;

    const dataToUpdate: ProjectType = {
      is_active,
      protocol,
      host,
      reporting_frequency,
    };

    if (editMode) {
      setButtonLoader(true);
      if (projectInfo === createProjectdata.current_id) {
        if (is_active === 0) {
          showToast(
            "This project is being used right now, you are not allowed to deactivate!",
            "error"
          );

          return;
        }
      }
      const response = await UpdateProject({
        ...dataToUpdate,
        id: createProjectdata.current_id,
      });

      if (response.responseType === "success") {
        showToast(response.message, "success");
      }
      setButtonLoader(false);

      return;
    } else {
      setButtonLoader(true);
      try {
        const newProject = await createProject({
          ...dataToUpdate,
          is_experimental: 0,
          account_id: account.id,
          plan_id: accountPlanId,
        });

        if (newProject.status === 200) {
          const data = await dispatch(handleGetAllProjects());
          updatePages(data.payload.id);
          showToast(newProject.data.message, "success");

          setShowModal(false);
        }
        setButtonLoader(false);
      } catch (e: any) {
        setButtonLoader(false);
        setError("host", { type: "manual", message: e.response.data.message });
      }
    }
  };

  const handleOpenEditDataModel = (
    e: React.MouseEvent<HTMLButtonElement>,
    project: ProjectType
  ) => {
    e.stopPropagation();
    const { is_active, host, protocol, reporting_frequency } = project;
    const newProjectObject = { is_active, host, protocol, reporting_frequency };
    setEditMode(1);
    setModalToShow("create");

    setProjectData({ ...newProjectObject, current_id: project.id });
  };

  const updatePages = async (id: number) => {
    const getLastPage = await showPages({
      project_id: id,
    });
    const { data } = getLastPage?.data;
    dispatch(
      setURI({
        id: data[0].id,
        uuid: data[0].uuid,
        uri: data[0].uri,
        project_id: id,
      })
    );
  };

  const onOkConfirm = async (id: number) => {
    const response = await deleteProject(id);
    if (response?.status === 200) {
      loadProjects();
      const getNewProject = await dispatch(handleGetAllProjects());
      updatePages(getNewProject.payload.id);
      showToast(response.data.message, "success");
    } else if (response?.status === 400) {
      showToast(response.data.message, "error");
    }
    setShowConfirmationModal(false);
  };
  const handleDeleteProject = async (
    e: React.MouseEvent<HTMLButtonElement>,
    current: ProjectType
  ) => {
    e.stopPropagation();
    setCurrentProject(current);
    setShowConfirmationModal(true);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value.toLocaleLowerCase();
    if (searchTerm.length === 0) {
      setProjects((prev) => ({ ...prev, data: prev.cloneData }));
      setDataFound("");
      return;
    }
    const filteredData = projects.cloneData.filter((data: { host: string }) => {
      if (data.host.toLocaleLowerCase().includes(searchTerm)) {
        return data;
      }
      return "";
    });
    setDataFound(`${filteredData.length} data found!`);
    setProjects((prev) => ({ ...prev, data: filteredData }));
  };
  const handleSetProject = (project: Project) => {
    if (project.id === projectInfo) {
      return;
    }
    if (!project.is_active) {
      showToast("This project is Inactive, activate first use this!", "error");

      return;
    }
    const newProject = {
      projectId: project.id,
      projectName: project.host,
      projectUUID: project.uuid,
    };

    dispatch(setURI({ id: 0, uri: "/" }));
    dispatch(setProject(newProject));
    dispatch(handleGetPages(project.id));

    navigate(`/project/${project.uuid}`);
    setShowModal(false);
  };

  return (
    <>
      <button
        className="w-9 h-9 md:w-10 md:h-10 mr-2 md:mr-3 group/hs relative text-white flex items-center border-solid border-2 border-primaryblue border-bg-primaryblue hover:bg-white hover:text-primaryblue bg-primaryblue text-14px font-bold  p-2 leading-7 rounded-md group/hs"
        id="add-project"
        type="button"
        onClick={() => {
          setProjectData(INITIAL_PROJECT);
          setModalToShow("create");
          setEditMode(0);
          setShowModal(true);
        }}
      >
        <svg
          width="19"
          height="23"
          className="fill-white group-hover/hs:fill-primaryblue"
          viewBox="0 0 19 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.69458 0H13.8881C14.4387 0 14.8877 0.449017 14.8877 0.999579V15.3005C14.8748 15.3003 14.8621 15.3 14.8492 15.3C12.6153 15.3 10.8043 17.1109 10.8043 19.3449C10.8043 19.4789 10.811 19.6113 10.8237 19.742H0.999579C0.449017 19.742 0 19.2929 0 18.7424V3.69458H2.14444C2.99855 3.69458 3.69463 2.99855 3.69463 2.14444V0H3.69458ZM2.51416 10.9761H12.3735C12.531 10.9761 12.6587 10.8484 12.6587 10.6908C12.6587 10.5333 12.531 10.4056 12.3735 10.4056H2.51416C2.35661 10.4056 2.22892 10.5333 2.22892 10.6908C2.22892 10.8484 2.35661 10.9761 2.51416 10.9761ZM2.51416 17.5397H7.44384C7.60139 17.5397 7.72908 17.412 7.72908 17.2545C7.72908 17.0969 7.60139 16.9692 7.44384 16.9692H2.51416C2.35661 16.9692 2.22892 17.0969 2.22892 17.2545C2.22892 17.412 2.35661 17.5397 2.51416 17.5397ZM2.51416 15.3518H12.3735C12.531 15.3518 12.6587 15.2241 12.6587 15.0666C12.6587 14.909 12.531 14.7813 12.3735 14.7813H2.51416C2.35661 14.7813 2.22892 14.909 2.22892 15.0666C2.22892 15.2241 2.35661 15.3518 2.51416 15.3518ZM2.51416 13.1639H12.3735C12.531 13.1639 12.6587 13.0362 12.6587 12.8787C12.6587 12.7211 12.531 12.5934 12.3735 12.5934H2.51416C2.35661 12.5934 2.22892 12.7211 2.22892 12.8787C2.22892 13.0362 2.35661 13.1639 2.51416 13.1639ZM7.79511 2.50556C7.5529 2.48041 7.30783 2.48136 7.0658 2.50831C6.88995 2.5279 6.75574 2.66567 6.74077 2.842L6.69931 3.33076C6.67668 3.59727 6.3651 3.72777 6.1593 3.55695L5.78207 3.2439C5.64596 3.13095 5.45371 3.1299 5.31642 3.24138C5.12711 3.39508 4.95478 3.56884 4.80265 3.75943C4.69231 3.89763 4.69492 4.08978 4.80897 4.22499L5.12497 4.59965C5.29745 4.80408 5.16942 5.1167 4.9031 5.14147L4.41482 5.18687C4.23873 5.20322 4.10205 5.33843 4.0838 5.51437C4.05865 5.75649 4.0596 6.00175 4.08655 6.24369C4.10614 6.41954 4.24391 6.55374 4.42024 6.56872L4.909 6.61017C5.17551 6.6328 5.30601 6.94438 5.13519 7.15018L4.82214 7.52741C4.70914 7.66352 4.70814 7.85577 4.81962 7.99307C4.97327 8.18237 5.14708 8.35471 5.33767 8.50683C5.47587 8.61718 5.66807 8.61456 5.80323 8.50056L6.17789 8.18456C6.38231 8.01208 6.69494 8.14011 6.71971 8.40643L6.76511 8.89472C6.78146 9.07085 6.91667 9.20748 7.09256 9.22574C7.33473 9.25089 7.5799 9.24994 7.82188 9.22298C7.99773 9.20339 8.13193 9.06562 8.14691 8.8893L8.18841 8.40049C8.21104 8.13398 8.52262 8.00348 8.72842 8.17429L9.10565 8.48734C9.24176 8.6003 9.43401 8.60135 9.57131 8.48986C9.76061 8.33617 9.93295 8.16241 10.0851 7.97182C10.1954 7.83362 10.1928 7.64141 10.0788 7.50626L9.76275 7.13159C9.59028 6.92717 9.7183 6.61455 9.98462 6.58978L10.4729 6.54438C10.649 6.52802 10.7857 6.39282 10.8039 6.21692C10.8291 5.9748 10.8281 5.72954 10.8012 5.48761C10.7816 5.31171 10.6438 5.17755 10.4675 5.16258L9.97873 5.12112C9.71222 5.09849 9.58172 4.78691 9.75253 4.58111L10.0656 4.20388C10.1786 4.06777 10.1796 3.87552 10.0681 3.73823C9.91445 3.54892 9.74065 3.37659 9.55006 3.22446C9.41186 3.11412 9.21965 3.11673 9.0845 3.23073L8.70983 3.54678C8.50541 3.71926 8.19279 3.59118 8.16802 3.32486L8.12262 2.83658C8.10626 2.66044 7.97106 2.52381 7.79511 2.50556ZM7.44379 4.34916C6.60628 4.34916 5.92731 5.02813 5.92731 5.86565C5.92731 6.70316 6.60623 7.38213 7.44379 7.38213C8.28135 7.38213 8.96027 6.70316 8.96027 5.86565C8.96027 5.02813 8.28131 4.34916 7.44379 4.34916ZM0.285193 3.12405H2.14439C2.68364 3.12405 3.12405 2.68364 3.12405 2.14439V0.285241L0.285193 3.12405Z"
          />
          <circle cx="14.9087" cy="19.3918" r="3.47319" />
          <path
            d="M13.6504 19.3627H16.1559"
            stroke="#2E9EFF"
            strokeWidth="0.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14.9031 18.1099V20.6154"
            stroke="#2E9EFF"
            strokeWidth="0.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <button
        className="w-9 h-9 md:w-10 md:h-10 group/hs relative text-white flex items-center border-solid border-2 border-primaryblue border-bg-primaryblue hover:bg-white hover:text-primaryblue bg-primaryblue text-14px font-bold  p-2 leading-7 rounded-md group/hs"
        type="button"
        onClick={() => {
          setShowModal(true);
          setEditMode(0);
          setModalToShow("show");
        }}
      >
        <svg
          width="28"
          height="22"
          className="fill-white group-hover/hs:fill-primaryblue"
          viewBox="0 0 28 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.75 2C0.75 1.30964 1.30964 0.75 2 0.75H26.466C27.1563 0.75 27.716 1.30964 27.716 2C27.716 2.69036 27.1563 3.25 26.466 3.25H2C1.30964 3.25 0.75 2.69036 0.75 2ZM0.75 8.1165C0.75 7.42614 1.30964 6.8665 2 6.8665H17.2912C17.9816 6.8665 18.5412 7.42614 18.5412 8.1165C18.5412 8.80685 17.9816 9.3665 17.2912 9.3665H2C1.30964 9.3665 0.75 8.80685 0.75 8.1165ZM0.75 14.233C0.75 13.5426 1.30964 12.983 2 12.983H26.466C27.1563 12.983 27.716 13.5426 27.716 14.233C27.716 14.9233 27.1563 15.483 26.466 15.483H2C1.30964 15.483 0.75 14.9233 0.75 14.233ZM0.75 20.3495C0.75 19.6591 1.30964 19.0995 2 19.0995H17.2912C17.9816 19.0995 18.5412 19.6591 18.5412 20.3495C18.5412 21.0398 17.9816 21.5995 17.2912 21.5995H2C1.30964 21.5995 0.75 21.0398 0.75 20.3495Z"
          />
        </svg>
      </button>
      {showModal && modalToShow === "create" ? (
        <CreateOrEditProjectsModal
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          editMode={editMode}
          showModal={showModal}
          setShowModal={setShowModal}
          register={register}
          setProjectData={setProjectData}
          santizeUrl={santizeUrl}
          errors={errors as string}
          buttonLoader={buttonLoader}
        />
      ) : showModal && modalToShow === "show" ? (
        <ShowProjectsModal
          showModal={showModal}
          setShowModal={setShowModal}
          handleSearch={handleSearch}
          dataFound={dataFound}
          projects={projects}
          projectInfo={projectInfo}
          handleSetProject={handleSetProject}
          handleOpenEditDataModel={handleOpenEditDataModel as VoidFunction}
          handleDeleteProject={handleDeleteProject}
        />
      ) : null}

      {showConfirmationModal && (
        <ConfirmationModal
          element={currentProject}
          showConfirmationModal={showConfirmationModal}
          setShowConfirmationModal={setShowConfirmationModal}
          onOk={onOkConfirm}
        />
      )}
    </>
  );
};

export default ProjectModal;
